<template>
  <FormField :id="id" :label="label" :info="info" :error="error">
    <div
      class="relative flex h-14 items-center overflow-hidden rounded-xl border transition-colors focus-within:border-blue-bright"
      :class="[
        off ? 'bg-surface-muted' : 'bg-white',
        error ? 'border-red-600' : 'border-line',
        { 'field-flash': flash },
      ]"
      :style="{ '--flash-delay': `${flashDelay}ms` }"
    >
      <img :src="icon" alt="" class="ml-4 size-[22px] shrink-0" :class="{ 'opacity-40': off }" />
      <input
        :id="id"
        :value="modelValue"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        maxlength="6"
        :placeholder="placeholder"
        :disabled="disabled || locked"
        :aria-invalid="error ? true : undefined"
        :aria-describedby="describedBy"
        class="h-full min-w-0 flex-1 bg-transparent px-2.5 text-base font-normal outline-none placeholder:text-line-strong disabled:cursor-not-allowed"
        :class="textColour"
        @input="onInput"
      />
      <span class="h-full w-px shrink-0 bg-line" aria-hidden="true" />
      <!-- The minimum width fits the widest unit (kWh per jaar), so the divider sits at the same
           spot in all three fields instead of following each unit's own width. -->
      <span :id="`${id}-unit`" class="min-w-[123px] px-4 text-base font-medium whitespace-nowrap text-muted">{{
        unit
      }}</span>
    </div>
  </FormField>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import FormField from '@/components/form/FormField.vue'

const props = withDefaults(
  defineProps<{
    id: string
    label: string
    unit: string
    /** URL of the coloured icon from the design's icon set. */
    icon: string
    modelValue: string
    placeholder?: string
    info?: string
    error?: string
    /** Not applicable right now: greyed out. */
    disabled?: boolean
    /** Frozen while the calculation runs: greyed out, but the value stays readable. */
    locked?: boolean
    /** A value just landed here from elsewhere, like the estimation modal: flash it briefly. */
    flash?: boolean
    /** Staggers the flash across fields, so the eye follows them top to bottom. */
    flashDelay?: number
  }>(),
  {
    placeholder: undefined,
    info: undefined,
    error: undefined,
    disabled: false,
    locked: false,
    flash: false,
    flashDelay: 0,
  },
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

// On hold or not applicable: the field steps back, so it is clear why typing does nothing. The
// value it holds stays readable, unlike a field that has nothing to say at all.
const off = computed(() => props.disabled || props.locked)

const textColour = computed(() => {
  if (props.disabled) {
    return 'text-muted'
  }

  return props.locked ? 'text-body' : 'text-label'
})

const describedBy = computed(() =>
  [props.error ? `${props.id}-error` : null, `${props.id}-unit`].filter(Boolean).join(' '),
)

// The same ceiling as the choice engine: six digits. Real bounds live in the calculation service.
function onInput(event: Event) {
  const input = event.target as HTMLInputElement
  input.value = input.value.replace(/\D/g, '').slice(0, 6)
  emit('update:modelValue', input.value)
}
</script>
